<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Address/AddressFormFields.vue
import type { Schemas } from '@shopware/api-client/api-types';
import { useCustomerGroupStore } from '~/stores/CustomerGroupsStore';

const props = withDefaults(
    defineProps<{
        addressType?: 'billingAddress' | 'shippingAddress';
        initialAddress?: Schemas['CustomerAddress'] | null;
        errorNameNested?: boolean;
        inCheckout?: boolean;
        allowGuest?: boolean;
        inAccount?: boolean;
        requestInitialVat?: boolean;
    }>(),
    {
        addressType: 'billingAddress',
        initialAddress: null,
        errorNameNested: true,
        inCheckout: false,
        allowGuest: false,
        inAccount: false,
        requestInitialVat: false,
    },
);

const { t } = useI18n();
const sessionContext = useSessionContext();
const { getCountries } = useCountries();
const { getSalutations } = useSalutations();
const { errorOfField, entityArrayToOptions } = useFormkitHelper();
const formErrorStore = useFormErrorStore();
//CUSTOM: IDS-49 added CustomerGroups store to fetch them from BE
const customerGroupStore = useCustomerGroupStore();
const { togglePasswordField } = useCustomFormkitHelper();
//CUSTOM: VAT validation composable
const { isValid: isValidVat, getPattern: getVatPattern } = useVatValidation();

const passwordRequired = ref(true);

await customerGroupStore.fetchCustomerGroups();
const { allCustomerGroups, currentCustomerGroup } = storeToRefs(customerGroupStore);

const countryOptions = computed(() => entityArrayToOptions<Schemas['Country']>(getCountries.value, 'name', true) ?? []);

const currentCountry = computed(() =>
    props.initialAddress ? props.initialAddress?.countryId : sessionContext.countryId.value,
);

// Get the ISO code of the selected country for VAT validation
const selectedCountryIso = computed(() => {
    const countryId = props.initialAddress?.countryId || sessionContext.countryId.value;
    const country = getCountries.value?.find(c => c.id === countryId);
    return country?.iso?.toUpperCase() || 'DE'; // Default to DE if not found
});

// Custom VAT validation function based on country
const validateVat = (node: any) => {
    const vatNumber = node.value;
    const countryIso = selectedCountryIso.value;

    if (!vatNumber) {
        return true; // Let the 'required' rule handle empty values
    }

    return isValidVat(vatNumber, countryIso);
};

const salutationOptions = computed(
    () => entityArrayToOptions<Schemas['Salutation']>(getSalutations.value, 'displayName', true) ?? [],
);

//CUSTOM: IDS-49 added customer type options, VAT ID, and conditional VAT requirement + dynamically loading customer groups
const vatId = ref(props?.initialAddress?.customFields?.vatId);
const selectedCustomerGroup = ref(currentCustomerGroup.value?.id);
const customerGroupOptions = computed(() =>
    (allCustomerGroups.value ?? []).map(group => ({
        value: group.id,
        label: group.name,
        hasVat: group.customFields.hasVat,
    })),
);

const requestVatOptions = ref([

    {
        value: true,
        label: t('account.register.requestVAT.yes'),
    },
    {
        value: false,
        label: t('account.register.requestVAT.no'),
    }
]);

const requestVat = ref(vatId.value || props.requestInitialVat ? true : false);


onUnmounted(() => {
    formErrorStore.$reset;
});

//Custom IDS-132 guest check required in the register form in the checkout page
const emit = defineEmits(['update:isGuest']);
const isGuest = ref(false);
watch(isGuest, newValue => {
    passwordRequired.value = !newValue;
    emit('update:isGuest', newValue);
});
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<FormKit
        type="select"
        :label="t('account.register.salutation.label')"
        name="salutationId"
        :value="initialAddress?.salutationId"
        :placeholder="t('account.register.salutation.placeholder')"
        :error="errorOfField('salutationId', formErrorStore.apiErrors)"
        validation="required"
        :classes="{
            outer: {
                'col-span-2 md:col-span-1 col-1': true,
            },
        }"
        :options="salutationOptions"
        :validation-messages="{
            required: t('account.register.salutation.required'),
        }"
    />

    <div class="col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2">
        <FormKit
            type="text"
            :label="t('account.register.firstName.label')"
            name="firstName"
            :value="initialAddress?.firstName"
            :placeholder="t('account.register.firstName.placeholder')"
            :validation="[['required'], ['matches', /^(?!\s)[\p{L}\p{M}\s'-]+$/u]]"
            validation-visibility="blur"
            :validation-messages="{
                required: t('account.register.firstName.required'),
                matches: t('account.register.firstName.error'),
            }"
        />
        <FormKit
            type="text"
            :label="t('account.register.lastName.label')"
            name="lastName"
            :value="initialAddress?.lastName"
            :placeholder="t('account.register.lastName.placeholder')"
            :validation="[['required'], ['matches', /^(?!\s)[\p{L}\p{M}\s'-]+$/u]]"
            validation-visibility="blur"
            :validation-messages="{
                required: t('account.register.lastName.required'),
                matches: t('account.register.lastName.error'),
            }"
        />
    </div>

    <FormKit
        v-if="inCheckout && allowGuest"
        v-model="isGuest"
        type="checkbox"
        :label="t('account.register.guest.toggle')"
        name="guest"
        decorator-icon="check"
        validation="optional"
        :classes="{
            outer: 'col-span-2 pb-3',
        }"
    />

    <div
        v-if="inCheckout && addressType === 'billingAddress'"
        class="col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2"
    >
        <FormKit
            type="email"
            :label="t('account.register.email.label')"
            name="email"
            :placeholder="t('account.register.email.placeholder')"
            :errors="errorOfField('email', formErrorStore.apiErrors)"
            :validation="[['required'], ['email']]"
            validation-visibility="blur"
            :validation-messages="{
                email: t('account.register.email.error'),
                required: t('account.register.email.required'),
            }"
        />
        <FormKit
            v-if="passwordRequired"
            type="password"
            :label="t('account.register.password.label')"
            name="password"
            :placeholder="t('account.register.password.placeholder')"
            :errors="errorOfField('password', formErrorStore.apiErrors)"
            :validation="[['required'], ['matches', /^[^\s]+$/u]]"
            validation-visibility="blur"
            suffix-icon="eye-strike-through"
            :validation-messages="{
                password: t('account.register.password.error'),
                required: t('account.register.password.required'),
            }"
            @suffix-icon-click="togglePasswordField"
        />
    </div>

    <template v-if="addressType === 'billingAddress' || !inCheckout">
        <!-- Customer Group Selection -->
        <FormKit
            v-show="!inAccount"
            v-model="selectedCustomerGroup"
            type="select"
            :label="t('account.register.customerGroup.label')"
            name="requestedGroupId"
            :placeholder="t('account.register.customerGroup.placeholder')"
            :error="errorOfField('customerGroupOptions', formErrorStore.apiErrors)"
            validation="required"
            :options="customerGroupOptions"
            :classes="{
                outer: {
                    'col-span-2 md:col-span-1 col-1': true,
                },
            }"
            :validation-messages="{
                required: t('account.register.customerGroup.required'),
            }"
        />

            <FormKit
                type="text"
                :label="t('account.register.company.label')"
                name="company"
                :value="initialAddress?.company"
                :placeholder="t('account.register.company.placeholder')"
                validation-visibility="blur"
                :classes="{
                    outer: {
                    'col-span-2 md:col-span-1 col-1': true,
                    },
                }"
            />

            <!--CUSTOM IDS-49: added field for vat id-->
            <FormKit
                v-model="requestVat"
                type="select"
                :label="t('account.register.requestVAT.label')"
                name="requestVat"
                validation="required"
                :options="requestVatOptions"
                :classes="{
                    outer: {
                        'col-span-2 md:col-span-1 col-1': true,
                    },
                }"
            />
            <FormKit
                v-if="requestVat"
                v-model="vatId"
                type="text"
                :label="t('account.register.vatId.label')"
                name="vatIds"
                :placeholder="t('account.register.vatId.placeholder')"
                :validation="[['required'], ['validateVat']]"
                :validation-rules="{ validateVat }"
                validation-visibility="blur"
                :validation-messages="{
                    required: t('account.register.vatId.required'),
                    validateVat: t('account.register.vatId.error'),
                }"
                :classes="{
                    input: 'border-gray-300',
                    inputInvalid: 'border-red-500',
                    message: 'text-red-500 text-sm mt-1',
                    outer: {
                        'col-span-2 md:col-span-1 col-1': true,
                    },
                }"
            />
    </template>
    <div
        v-if="!inAccount && addressType === 'billingAddress'"
        class="col-span-2 col-start-1 mt-2 font-extrabold"
    >
        {{ t('checkout.personal.headingAddress') }}
    </div>
    <FormKit
        type="group"
        :name="addressType"
    >
        <FormKit
            type="text"
            :label="t('account.register.street.label')"
            autocomplete="street-address"
            name="street"
            :value="initialAddress?.street"
            :placeholder="t('account.register.street.placeholder')"
            :validation="[['required'], ['matches', /^(?!\s)[\p{L}\p{M}0-9\s.'-\/]+$/u]]"
            validation-visibility="blur"
            :validation-messages="{
                matches: t('account.register.street.error'),
                required: t('account.register.street.required'),
            }"
            :classes="{
                outer: {
                    'col-span-2 md:col-span-1 col-1': true,
                },
            }"
        />

        <FormKit
            type="tel"
            :label="t('account.register.phoneNumber.label')"
            name="phoneNumber"
            :value="initialAddress?.phoneNumber"
            :placeholder="t('account.register.phoneNumber.placeholder')"
            :validation="[['required'], ['matches', /^(\+\d{1,3})?\d{8,14}$/u]]"
            validation-visibility="blur"
            :validation-messages="{
                required: t('account.register.phoneNumber.required'),
                matches: t('account.register.phoneNumber.error'),
            }"
            :classes="{
                outer: {
                    'col-span-2 md:col-span-1 col-1 md:col-start-1': true,
                },
            }"
        />

        <div class="col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormKit
                type="text"
                :label="t('account.register.zipCode.label')"
                name="zipcode"
                :value="initialAddress?.zipcode"
                :placeholder="t('account.register.zipCode.placeholder')"
                :errors="
                    errorOfField(errorNameNested ? `${addressType}[zipcode]` : 'zipcode', formErrorStore.apiErrors)
                "
                :validation="[['required'], ['matches', /^\d+$/]]"
                validation-visibility="blur"
                :validation-messages="{
                    required: t('account.register.zipCode.required'),
                    matches: t('account.register.zipCode.error'),
                }"
            />
            <FormKit
                type="text"
                :label="t('account.register.city.label')"
                name="city"
                :value="initialAddress?.city"
                :placeholder="t('account.register.city.placeholder')"
                :validation="[['required'], ['matches', /^(?!\s)[\p{L}\p{M}\s.'-]+$/u]]"
                validation-visibility="blur"
                :validation-messages="{
                    required: t('account.register.city.required'),
                    matches: t('account.register.city.error'),
                }"
            />
        </div>
        <FormKit
            v-if="currentCountry"
            type="select"
            :label="t('account.register.country.label')"
            name="countryId"
            :placeholder="t('account.register.country.placeholder')"
            validation="required"
            :options="countryOptions"
            :value="currentCountry"
            :classes="{
                outer: {
                    'col-span-2 md:col-span-1 col-1': true,
                },
            }"
        />
    </FormKit>
</FormKitLazyProvider>

</template>
